import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Phone, Mail, Clock, Send, Heart } from 'lucide-react';
import { churchInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Mock submission
    toast({
      title: "Message Sent Successfully!",
      description: "We've received your message and will respond soon. God bless you!",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: 'general',
      message: ''
    });
  };

  const handlePrayerRequest = (e) => {
    e.preventDefault();
    setFormData({...formData, subject: 'prayer'});
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#800020] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">Contact Us</h1>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-[#fef3c7]">
            We'd love to hear from you. Reach out with questions, prayer requests, or just to say hello!
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="bg-[#fffbeb] border-2 border-[#800020] hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]">
                  <MapPin className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-[#800020] mb-2">Address</h3>
                <p className="text-gray-700 text-sm">{churchInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="bg-[#fffbeb] border-2 border-[#800020] hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]">
                  <Phone className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-[#800020] mb-2">Phone</h3>
                <a href={`tel:${churchInfo.phone}`} className="text-gray-700 text-sm hover:text-[#D4AF37]">
                  {churchInfo.phone}
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#fffbeb] border-2 border-[#800020] hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]">
                  <Mail className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-[#800020] mb-2">Email</h3>
                <a href={`mailto:${churchInfo.email}`} className="text-gray-700 text-sm hover:text-[#D4AF37]">
                  {churchInfo.email}
                </a>
              </CardContent>
            </Card>

            <Card className="bg-[#fffbeb] border-2 border-[#800020] hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]">
                  <Clock className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-[#800020] mb-2">Office Hours</h3>
                {churchInfo.officeHours.map((schedule, index) => (
                  <p key={index} className="text-gray-700 text-sm">
                    {schedule.days}: {schedule.hours}
                  </p>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Contact Form and Map */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#800020] mb-6">Send Us a Message</h2>
              <Card className="border-2 border-[#800020]">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-[#800020] font-semibold">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="border-[#800020] focus:ring-[#D4AF37] mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#800020] font-semibold">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="border-[#800020] focus:ring-[#D4AF37] mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-[#800020] font-semibold">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="border-[#800020] focus:ring-[#D4AF37] mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-[#800020] font-semibold">Subject *</Label>
                      <select
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="w-full mt-2 px-3 py-2 border-2 border-[#800020] rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
                        required
                      >
                        <option value="general">General Inquiry</option>
                        <option value="prayer">Prayer Request</option>
                        <option value="visit">Plan a Visit</option>
                        <option value="ministry">Ministry Information</option>
                        <option value="pastoral">Pastoral Care</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#800020] font-semibold">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="border-[#800020] focus:ring-[#D4AF37] mt-2 min-h-[150px]"
                        required
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#D4AF37] hover:bg-[#B8860B] text-white py-6 text-lg font-semibold"
                    >
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Map and Additional Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#800020] mb-6">Find Us</h2>
              <div className="rounded-lg overflow-hidden shadow-xl mb-6">
                <iframe
                  title="Church Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3401.234!2d-84.2324!3d31.7219!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDQzJzE4LjgiTiA4NMKwMTMnNTYuNiJX!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                />
              </div>

              {/* Prayer Request CTA */}
              <Card className="bg-[#800020] text-white border-2 border-[#800020]">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Need Prayer?</h3>
                  <p className="text-[#fef3c7] mb-6">
                    We believe in the power of prayer. Our prayer team is here to lift you up before the throne of grace.
                  </p>
                  <Button 
                    onClick={handlePrayerRequest}
                    className="bg-[#D4AF37] hover:bg-[#B8860B] text-white w-full"
                  >
                    Submit Prayer Request
                  </Button>
                </CardContent>
              </Card>

              {/* Directions */}
              <Card className="bg-[#fffbeb] border-2 border-[#800020] mt-6">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#800020] mb-4">Directions</h3>
                  <p className="text-gray-700 mb-4">
                    Mt. Olive Missionary Baptist Church is located in the heart of Americus, Georgia, easily accessible from major highways.
                  </p>
                  <Button 
                    className="w-full bg-[#800020] hover:bg-[#8B0000] text-white"
                    onClick={() => window.open('https://www.google.com/maps/dir/?api=1&destination=420+E+Jefferson+St,+Americus,+GA+31709', '_blank')}
                  >
                    Get Directions
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Visit Information */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Planning Your First Visit?</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-2 border-[#800020]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#800020] mb-3">What to Expect</h3>
                <p className="text-gray-700">
                  Our services are warm, welcoming, and spirit-filled. Come as you are! We have greeters at the door ready to welcome you and answer any questions.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-[#800020]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#800020] mb-3">Parking & Entrance</h3>
                <p className="text-gray-700">
                  We have ample parking available around the church building. The main entrance is on East Jefferson Street. Handicap accessible parking and entrance available.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-[#800020]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#800020] mb-3">Children's Ministry</h3>
                <p className="text-gray-700">
                  We offer Sunday School for all ages at 9:00 AM. Children are welcome in worship service, and nursery care is available for infants and toddlers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
